<template>
  <div id="request-wizard">
    <h2 class="mb-4 text-center">{{ title }}</h2>

    <FormWizard
      v-bind="$attrs"
      @finish="showSubmitModal = true"
      @update:activeStep="activeStep = $event"
    >
      <template #nav>
        <WizardNav
          class="request-wizard-nav stylish-scroll"
          :hide-unvisited="true"
          :aria-label="title"
          nav-button-class="btn req-wizard-btn"
          nav-button-active-class="btn-accent-warm req-wizard-btn-active"
          nav-button-locked-class="btn-outline-primary req-wizard-btn-locked"
          nav-button-unlocked-class="btn-primary"
          nav-label-class="req-wizard-nav-label"
          prog-bar-class="req-wizard-prog-bar"
        />
      </template>

      <template #default="{ transitioning }">
        <div :style="{ 'overflow': transitioning ? 'hidden' : 'visible' }"
          class="mt-4"
        >
          <slot></slot>
        </div>
      </template>
    </FormWizard>

    <SubmitFormModal
      :show="showSubmitModal"
      :submit-fn="submit"
      @close="onCloseModal"
      @success="onSuccess"
    >
      <template #submitting-status>Submitting your request...</template>

      <template v-slot="{ confirmationNumber }">
        <div class="d-flex flex-column align-items-center gap-3">
          <div class="bi bi-check-circle text-success icon-zoom-in status-icon"></div>
          <h4>Your request has been submitted!</h4>

          <div v-if="showConfirmationMessage" class="w-100">
            <AlertMessage type="success" slim>
              <p>Please keep the below confirmation number for your records:</p>
              <b class="fs-5">{{ confirmationNumber }}</b>
              <IconButton
                class="ms-1"
                :iconClass="`bi bi-clipboard${confirmationNumberCopied ? '-check' : ''}`"
                :title="confirmationNumberCopied
                  ? 'Confirmation number has been copied' : 'Copy the confirmation number'"
                @click="copyConfirmationNum(confirmationNumber)"
              />
            </AlertMessage>

            <p class="text-danger small">
              <span class="bi bi-exclamation-circle"></span>
              The confirmation number is only visible until you close this page.
            </p>
          </div>

          <slot name="success-message"></slot>

          <button v-if="showNewRequestButton" type="button"
            class="btn btn-outline-primary w-100 mt-4"
            @click="reload"
          >Click here to start a new request</button>

        </div>
      </template>

      <template #error-body>
        <div>
          If you still experience issues in submitting your request, please submit your request via
          email to <a :href="`mailto:${config.supportEmail}`"
          >{{ config.supportEmail }}</a>.
        </div>
      </template>
    </SubmitFormModal>
  </div>
</template>

<script>
import FormWizard from '@ssi/vue-common/src/components/formWizard';
import WizardNav from '@ssi/vue-common/src/components/formWizard/WizardNav';
import { ADD } from '@ssi/vue-common/src/store/modules/pendingOps/actions';
import { ANY } from '@ssi/vue-common/src/store/modules/pendingOps/getters';
import IconButton from '../buttons/Icon';
import AlertMessage from '../AlertMessage';
import SubmitFormModal from '../Modals/SubmitFormModal';
import { submitFoiaRequest } from '../../api';

export default {
  inject: ['config'],

  components: {
    AlertMessage, FormWizard, IconButton, SubmitFormModal, WizardNav
  },

  props: {
    formData: {
      type: Object,
      required: true
    },

    bypassDirtyCheck: {
      type: Boolean,
      default: false
    },

    showConfirmationMessage: {
      type: Boolean,
      default: true
    },

    showNewRequestButton: {
      type: Boolean,
      default: true
    },

    title: {
      type: String,
      required: true
    }
  },

  data: () => ({
    activeStep: null,
    confirmationNumberCopied: false,
    showSubmitModal: false,
    submissionSucceeded: false
  }),

  methods: {
    async copyConfirmationNum(confirmationNumber) {
      await window.navigator.clipboard.writeText(confirmationNumber);
      this.confirmationNumberCopied = true;
      setTimeout(() => {
        this.confirmationNumberCopied = false;
      }, 5000);
    },

    handleBeforeUnload(event) {
      // NOTE: this is the same message that's displayed in Chrome,
      // but modern browsers actually ignore whatever value is returned.
      if (this.$store.getters[`pendingOps/${ANY}`] && !this.bypassDirtyCheck) {
        // eslint-disable-next-line no-param-reassign
        event.returnValue = 'Changes you made may not be saved.';
      }
    },

    onCloseModal() {
      if (this.submissionSucceeded) this.reload();
      else this.showSubmitModal = false;
    },

    onSuccess() {
      this.submissionSucceeded = true;
      this.clearPendingChanges();
    },

    reload() {
      window.location.reload();
    },

    submit(captchaToken) {
      return submitFoiaRequest(this.formData, captchaToken);
    }
  },

  watch: {
    activeStep(step, prevStep) {
      window.scrollTo({ top: 0, behavior: 'instant' });

      // Consider the wizard to be "dirty" after the user advances past the first step.
      if (prevStep && !this.clearPendingChanges) {
        this.$store.dispatch(`pendingOps/${ADD}`, new Promise((resolve) => {
          this.clearPendingChanges = resolve;
        }));
      }
    }
  },

  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },

  unmounted() {
    if (this.clearPendingChanges) this.clearPendingChanges();
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables";
@import "../../styles/animations";

:deep() {
  .request-wizard-nav {
    padding: 0.6em 0;
  }

  .req-wizard-btn {
    aspect-ratio: 1;
    border-radius: 50%;
    border-width: 3px;
    font-size: 0.8em;
    font-weight: 501;
    line-height: 1;
    padding-left: 0;
    padding-right: 0;
    transition: background-color, color, transform;
    transition-duration: 0.2s;
    width: 3em;
  }

  .req-wizard-btn-active {
    transform: scale(1.2);
  }

  .req-wizard-btn-locked {
    background-color: $body-bg;
  }

  .req-wizard-nav-label {
    color: $primary;
    font-weight: 500;
    font-size: 0.8em;
    margin-top: 0.75em;
    padding: 0 0.25em;
  }

  .req-wizard-prog-bar {
    background-color: $primary;
  }

  @media (max-width: 840px) {
    .req-wizard-nav-label {
      font-size: 0.7em;
    }
  }
}
</style>
